import { hasOtherEvents } from '@protector/entities/region/utils';
import { FeatureFlags } from '@protector/utils/constants';
import type { Indicator } from '@protector/utils/types';
import { useTimelineFieldDetailsStore } from '../timeline.fieldDetails.zustand';
import type { PhenologySample, TimelineWindowEvents, TimelineWindowEventsFromBff } from '../timeline.models';
import type { UsePrepareDataToAIInsightsProps } from './timeline.area-day-info.model';

const filterIndicators = (indicators: Indicator[]) => {
  return indicators.filter(indicator => {
    return !indicator?.feature_flags?.includes(FeatureFlags.NO_TIMELINE);
  });
};

export const filterEvents = (events: TimelineWindowEvents) => {
  if (!events?.monitoring_window?.samples) return events;

  const filteredSamples = events.monitoring_window.samples.map(sample => {
    const sampleCasted = sample;
    const filteredIndicators = filterIndicators(sampleCasted.phenomenon.indicators);

    // Only create new objects if indicators were actually filtered
    if (filteredIndicators.length === sampleCasted.phenomenon.indicators.length) {
      return sample; // No changes needed, return original
    }

    // Create minimal new objects only when necessary
    const newPhenomenon = {
      ...sampleCasted.phenomenon,
      indicators: filteredIndicators
    };

    return {
      ...sample,
      phenomenon: newPhenomenon
    };
  });

  const samplesChanged = filteredSamples.some((sample, index) => sample !== events.monitoring_window!.samples[index]);

  if (!samplesChanged) {
    return events;
  }

  const newMonitoringWindow = {
    ...events.monitoring_window,
    samples: filteredSamples
  };

  return {
    ...events,
    monitoring_window: newMonitoringWindow
  };
};

export const filterEventsWithCategories = (events: TimelineWindowEventsFromBff) => {
  if (!events?.monitoring_window?.samples) return events;

  const filteredSamples = events.monitoring_window.samples.map(category => {
    const filteredPhenomena = category.phenomena.map(phenomenon => {
      const filteredIndicators = filterIndicators(phenomenon.phenomenon.indicators);

      if (filteredIndicators.length === phenomenon.phenomenon.indicators.length) {
        return phenomenon;
      }

      const newPhenomenon = {
        ...phenomenon.phenomenon,
        indicators: filteredIndicators
      };

      return {
        ...phenomenon,
        phenomenon: newPhenomenon
      };
    });

    const phenomenaChanged = filteredPhenomena.some((phenomenon, index) => phenomenon !== category.phenomena[index]);

    if (!phenomenaChanged) {
      return category;
    }

    return {
      ...category,
      phenomena: filteredPhenomena
    };
  });

  const samplesChanged = filteredSamples.some((sample, index) => sample !== events.monitoring_window?.samples?.[index]);

  if (!samplesChanged) {
    return events;
  }

  const newMonitoringWindow = {
    ...events.monitoring_window,
    samples: filteredSamples
  };

  return {
    ...events,
    monitoring_window: newMonitoringWindow
  };
};

export const isValidData = (processedXRayData: UsePrepareDataToAIInsightsProps) => {
  const { area, events, region, workspaceId, companyId, propertyId, userId } = processedXRayData;
  return !!workspaceId && !!propertyId && !!companyId && !!region && !!events && !!area && !!userId;
};

export const removePhenologyFromTimelineWindowEvents = ({ dataSample }: { dataSample: PhenologySample }) => {
  const { currentWindowEvents } = useTimelineFieldDetailsStore.getState();

  const stageSamples = currentWindowEvents?.phenology_window?.samples.filter(
    ({ id, stage }) => id !== dataSample.id && stage.id === dataSample.stage.id
  );

  const filteredSamples = currentWindowEvents?.phenology_window?.samples.filter(phenologySample => phenologySample.id !== dataSample.id);

  if (!filteredSamples?.length && !hasOtherEvents(currentWindowEvents as TimelineWindowEvents)) {
    return;
  }

  if (!stageSamples?.length && currentWindowEvents?.phenology_window) {
    const newCurrentWindowEvents = {
      ...currentWindowEvents,
      phenology_window: {
        ...currentWindowEvents.phenology_window,
        samples: filteredSamples ?? [],
        stages: currentWindowEvents.phenology_window.stages.filter(({ stage }) => stage.id !== dataSample.stage.id)
      }
    };
    useTimelineFieldDetailsStore.setState({ currentWindowEvents: newCurrentWindowEvents });
    return;
  }

  const newCurrentWindowEventsPhenology = {
    ...currentWindowEvents,
    phenology_window: {
      ...currentWindowEvents?.phenology_window,
      samples: filteredSamples ?? [],
      type: currentWindowEvents?.phenology_window?.type,
      stages: currentWindowEvents?.phenology_window?.stages.map(phenologyStage => ({
        ...phenologyStage,
        count: phenologyStage.stage.id === dataSample.stage.id ? phenologyStage.count - 1 : phenologyStage.count
      }))
    }
  };
  useTimelineFieldDetailsStore.setState({ currentWindowEvents: newCurrentWindowEventsPhenology as TimelineWindowEventsFromBff });
};
